import BootIconButton from "components/common/custom/IconButton";
import { motion } from "framer-motion";
import moment from "moment";
import { Card, Col, Row } from "react-bootstrap";
import { BsDownload } from "react-icons/bs";
import { useSelector } from "react-redux";

const PreauthorizationClinialDocumentNested = () => {
  return (
    <div className="d-flex flex-column gap-3">
      <InvestigationDetails />
      <AttachmentDetails />
    </div>
  );
};

export default PreauthorizationClinialDocumentNested;

const InvestigationDetails = () => {
  const { preauthorizationRequest } = useSelector(
    (state) => state.preauthorization
  );
  const { claimRequestData } = useSelector((state) => state.claim);

  const cliamPreauthorization =
    preauthorizationRequest?.entry?.find(
      (e) => e.resource?.resourceType === "Claim"
    )?.resource ||
    claimRequestData?.entry?.find((e) => e.resource?.resourceType === "Claim")
      ?.resource;

  const getVitalSignValue = (targetCode) => {
    const item = cliamPreauthorization?.supportingInfo.find((entry) =>
      entry.category?.coding?.some((coding) => coding.code === targetCode)
    );

    if (item?.valueQuantity) {
      return `${item?.valueQuantity?.value} ${item?.valueQuantity?.code}`;
    } else if (item?.valueDateTime) {
      return `${item?.valueDateTime}`;
    } else if (item?.code) {
      return `${item?.code?.coding[0]?.code}`;
    } else if (item?.valueString) {
      return `${item?.valueString}`;
    }
    return "";
  };

  const getInvestigationResultCode = () => {
    const item = cliamPreauthorization?.supportingInfo.find((entry) =>
      entry.category?.coding?.some(
        (coding) => coding.code === "investigation-result"
      )
    );
    return item?.code?.coding[0]?.code || "";
  };

  const getOtherDescription = () => {
    const item = cliamPreauthorization?.supportingInfo.find((entry) =>
      entry.category?.coding?.some(
        (coding) => coding.code === "investigation-result"
      )
    );
    return item?.code?.text || "";
  };

  return (
    <motion.div
      style={{ border: "2px solid #d9f8eb" }}
      className="rounded p-3 position-relative mt-3"
      initial={{ opacity: 0, y: 50 }} // Starts faded out and moved down
      animate={{ opacity: 1, y: 0 }} // Fades in and moves to place
      exit={{ opacity: 0, y: 50 }} // Optional: for exit animation
      transition={{ duration: 0.5 }} // Smooth transition
    >
      {/* Floating Badge Header */}
      <div className="position-absolute top-0 start-40 translate-middle-y px-2">
        <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
          <span className="fs-11">Investigation Details</span>
        </span>
      </div>
      <Row>
        <Col xs={12}>
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Additional Supporting Info</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-600">
                {getVitalSignValue("info")}
              </span>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col xs={12} md={3}>
              <span className="fs-10 fw-bold">Investigation Result</span>
            </Col>
            <Col xs={12} md={9}>
              <span className="fs-10 fw-normal text-600">
                {
                  investigationResult?.find(
                    (item) =>
                      item?.investreslttype_code ===
                      getInvestigationResultCode()
                  )?.investreslttype_name
                }
              </span>
            </Col>
          </Row>
          <hr />
          {getInvestigationResultCode() === "other" && (
            <Row>
              <Col xs={12} md={3}>
                <span className="fs-10 fw-bold">Other Description</span>
              </Col>
              <Col xs={12} md={9}>
                <span className="fs-10 fw-normal text-600">
                  {getOtherDescription()}
                </span>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </motion.div>
  );
};

const AttachmentDetails = () => {
  const { preauthorizationRequest } = useSelector(
    (state) => state.preauthorization
  );

  const { claimRequestData } = useSelector((state) => state.claim);

  const cliamPreauthorization =
    preauthorizationRequest?.entry?.find(
      (e) => e.resource?.resourceType === "Claim"
    )?.resource ||
    claimRequestData?.entry?.find((e) => e.resource?.resourceType === "Claim")
      ?.resource;

  // Get all attachments from supportingInfo
  const attachments =
    cliamPreauthorization?.supportingInfo?.filter((entry) =>
      entry.category?.coding?.some((coding) => coding.code === "attachment")
    ) || [];

  // Include investigation result attachments if they have valueAttachment and contentType (like PDF)
  const investigationAttachments =
    cliamPreauthorization?.supportingInfo?.filter(
      (entry) =>
        entry.category?.coding?.some(
          (coding) => coding.code === "investigation-result"
        ) && entry.valueAttachment
    ) || [];

  const allAttachments = [...attachments, ...investigationAttachments];

  const getDocumentType = (contentType) => {
    switch (contentType) {
      case "application/pdf":
        return "PDF Document";
      case "image/png":
        return "PNG Image";
      case "image/jpeg":
        return "JPEG Image";
      default:
        return contentType?.split("/")?.pop()?.toUpperCase() || "File";
    }
  };

  const handleDownload = (attachment) => {
    const valueAttachment = attachment.valueAttachment || {};
    if (!valueAttachment?.data) return;

    try {
      // Decode base64 data
      const byteCharacters = atob(valueAttachment.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: valueAttachment.contentType });

      // Create temporary link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = valueAttachment.title || "document";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  return (
    allAttachments?.length > 0 && (
      <motion.div
        style={{ border: "2px solid #d9f8eb" }}
        className="rounded p-3 position-relative mt-3"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="position-absolute top-0 start-40 translate-middle-y px-2">
          <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
            <span className="fs-11">Attachment Details</span>
          </span>
        </div>
        <Row>
          {allAttachments.map((attachment, index) => {
            const valueAttachment = attachment.valueAttachment || {};
            return (
              <Card key={index} className="p-3 mb-3 rounded border-0">
                <Row className="align-items-center">
                  <Col xs="auto">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle border"
                      style={{
                        width: "40px",
                        height: "40px",
                        color: "#F59E0B",
                        borderColor: "#10B981",
                        boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
                      }}
                    >
                      <strong>{index + 1}</strong>
                    </div>
                  </Col>

                  <Col
                    xs={10}
                    md
                    className="d-flex flex-wrap justify-content-between align-items-center mt-3 mt-md-0"
                  >
                    <div className="d-flex flex-column">
                      <small className="text-muted fw-bold">
                        Document Name
                      </small>
                      <small className="text-600">
                        {valueAttachment.title || "N/A"}
                      </small>
                    </div>

                    <div className="d-flex flex-column">
                      <small className="text-muted fw-bold">
                        Document Type
                      </small>
                      <small className="text-600">
                        {getDocumentType(valueAttachment.contentType)}
                      </small>
                    </div>

                    <div className="d-flex flex-column">
                      <small className="text-muted fw-bold">
                        Creation Date
                      </small>
                      <small className="text-600">
                        {moment(valueAttachment.creation).format(
                          "DD-MM-YYYY"
                        ) || "N/A"}
                      </small>
                    </div>
                  </Col>

                  <Col xs="auto" className="text-end">
                    <BootIconButton
                      variant="success"
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "30px", height: "30px" }}
                      onClick={() => handleDownload(attachment)}
                    >
                      <BsDownload size={24} color="#fff" />
                    </BootIconButton>
                  </Col>
                </Row>
              </Card>
            );
          })}
        </Row>
      </motion.div>
    )
  );
};

const investigationResult = [
  {
    investreslttype_code: "IRA",
    investreslttype_name: "Investigation results attached",
  },
  {
    investreslttype_code: "INP",
    investreslttype_name: "Investigation not performed",
  },
  {
    investreslttype_code: "IRP",
    investreslttype_name: "Investigation results pending",
  },
  {
    investreslttype_code: "NA",
    investreslttype_name: "Not applicable",
  },
  {
    investreslttype_code: "other",
    investreslttype_name: "Other",
  },
];
